import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Field } from 'redux-form'
import {
  Input,
  InputError,
  LabeledField,
  fieldPropTypesWithValue,
} from 'lp-components'
import { RemovableThumbnail } from 'components'
import {
  convertCloudinarySrcUrlToFilename,
  getCloudinaryUploadErrorMessage,
} from 'utils'

/**
 * A redux-form Field component for uploading multiple files to Cloudinary.
 *
 * The field value is an array of objects: [{ src, filename, caption }].
 *
 * Optional per-item caption fields are rendered when `captionLabel` is provided.
 * Caption fields are registered as `<fieldName>[i].caption` in the parent form.
 *
 * @example
 * <Field
 *   name="spotlightImages"
 *   component={CloudinaryMultiFileInput}
 *   labelComponent={SpotlightImagesLabel}
 *   uploadPath={`/artists/users/spotlight-images/${artistId}`}
 *   uploadPreset={CLOUD_RESIZE_PRESET}
 *   accept=".png,.jpg,.jpeg"
 *   onUploadSuccess={() => flashSuccessMessage('Uploaded!')}
 *   onUploadFailure={flashErrorMessage}
 *   captionLabel={(filename) => `Add Image Caption for ${filename}`}
 * />
 */

const propTypes = {
  ...fieldPropTypesWithValue(
    PropTypes.arrayOf(
      PropTypes.shape({
        src: PropTypes.string.isRequired,
        filename: PropTypes.string.isRequired,
        caption: PropTypes.string,
      })
    )
  ),
  uploadPath: PropTypes.string.isRequired,
  uploadPreset: PropTypes.string,
  accept: PropTypes.string,
  onUploadSuccess: PropTypes.func,
  onUploadFailure: PropTypes.func,
  captionLabel: PropTypes.func, // (filename: string) => string
  disabled: PropTypes.bool,
}

const defaultProps = {
  accept: '.png,.jpg,.jpeg',
  disabled: false,
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target.result)
    reader.onerror = (err) => reject(err)
    reader.readAsDataURL(file)
  })
}

function uploadFileToCloudinary(fileData, fileName, uploadPath, uploadPreset) {
  const cloudName = process.env.CLOUDINARY_CLOUD_NAME
  const timestamp = Date.now()
  const bucket = `${process.env.CLOUDINARY_CLOUD_BUCKET}${uploadPath}/${timestamp}`
  const url = `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`
  const sanitizedName = fileName
    .replace(/\.[^.]+$/, '')
    .replace(/[\s?&#\\%<>]/gi, '_')
    .replace(/_+/gi, '_')

  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
    },
    mode: 'cors',
    body: JSON.stringify({
      file: fileData,
      folder: bucket,
      upload_preset: uploadPreset,
      public_id: sanitizedName,
    }),
  })
    .then((res) => {
      if (!res.ok) throw new Error('Cloudinary upload failed')
      return res.json()
    })
    .then((data) => data.secure_url)
}

function CloudinaryMultiFileInput(props) {
  const {
    input: { name, value, onChange },
    uploadPath,
    uploadPreset,
    accept,
    onUploadSuccess,
    onUploadFailure,
    captionLabel,
    disabled,
  } = props

  const images = value || []
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const errorId = `${name}-upload-error`

  const handleFilesChange = async (e) => {
    const files = Array.from(e.target.files)
    const inputEl = e.target
    if (!files.length) return

    setIsUploading(true)
    setUploadError(null)

    const accumulated = [...images]
    let encounteredError = false

    for (let i = 0; i < files.length; i += 1) {
      const selectedFile = files[i]
      try {
        const fileData = await readFileAsDataUrl(selectedFile)
        const secureUrl = await uploadFileToCloudinary(
          fileData,
          selectedFile.name,
          uploadPath,
          uploadPreset
        )
        const filename = convertCloudinarySrcUrlToFilename(secureUrl)
        accumulated.push({ src: secureUrl, filename, caption: '' })
        onChange([...accumulated])
      } catch (err) {
        encounteredError = true
        const message = getCloudinaryUploadErrorMessage(err)
        setUploadError(message)
        onUploadFailure?.(message)
        break
      }
    }

    if (!encounteredError) onUploadSuccess?.()
    setIsUploading(false)
    if (inputEl) inputEl.value = ''
  }

  const handleRemove = (indexToRemove) => {
    onChange(images.filter((_, i) => i !== indexToRemove))
  }

  return (
    <LabeledField {...props}>
      {!disabled && (
        <div className="fileupload fileupload-exists">
          <div
            className={`button-secondary-light${
              isUploading ? ' uploading' : ''
            }`}
          >
            <input
              id={name}
              name={name}
              type="file"
              multiple
              accept={accept}
              onChange={handleFilesChange}
              disabled={isUploading}
              aria-describedby={uploadError ? errorId : undefined}
              aria-labelledby={`${name}-label`}
            />
            <span className="fileupload-exists" id={`${name}-label`}>
              {isUploading ? 'Uploading...' : 'Select Files'}
            </span>
          </div>
        </div>
      )}
      {uploadError && (
        <InputError touched invalid error={uploadError} id={errorId} />
      )}
      {images.map((item, i) => (
        <div key={item.src} className="cloudinary-multi-file-item">
          <RemovableThumbnail
            value={item.src}
            image={{ filename: item.filename }}
            remove={disabled ? undefined : () => handleRemove(i)}
          />
          {captionLabel && (
            <Field
              name={`${name}[${i}].caption`}
              component={Input}
              label={captionLabel(item.filename)}
              disabled={disabled}
            />
          )}
        </div>
      ))}
    </LabeledField>
  )
}

CloudinaryMultiFileInput.propTypes = propTypes
CloudinaryMultiFileInput.defaultProps = defaultProps

export default CloudinaryMultiFileInput
